import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { CrossOutline, DropdownInput, Partner } from '@aslanpay/portal-common-lib';

import { PARTNER_COUNTRY_OPTIONS } from 'app/constants/partners';
import partnerFormSchema from 'app/utils/schemas/partner';

export interface EditPartnerFormFields {
  name: string;
  companyNumber: string;
  countryCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postCode?: string;
  addressCountryCode?: string;
}

interface EditPartnerFormProps {
  id: string;
  partner: Partner;
  onSubmit: (data: EditPartnerFormFields) => void;
  isLoading: boolean;
  onClose: () => void;
}

function EditPartnerForm({ id, partner, onSubmit, isLoading, onClose }: EditPartnerFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    resetField,
    setFocus,
    formState: { errors, isValid, isDirty },
  } = useForm<EditPartnerFormFields>({
    mode: 'onChange',
    defaultValues: {
      name: partner.name,
      companyNumber: partner.companyNumber ?? '',
      countryCode: partner.countryCode ?? '',
      addressLine1: partner.address?.addressLine1 ?? '',
      addressLine2: partner.address?.addressLine2 ?? '',
      addressLine3: partner.address?.addressLine3 ?? '',
      city: partner.address?.city ?? '',
      postCode: partner.address?.postCode ?? '',
      addressCountryCode: partner.address?.countryCode ?? '',
    },
    resolver: yupResolver(partnerFormSchema),
  });

  const watchName = watch('name');
  const watchCompanyNumber = watch('companyNumber');
  const watchAddressLine1 = watch('addressLine1');
  const watchAddressLine2 = watch('addressLine2');
  const watchAddressLine3 = watch('addressLine3');
  const watchCity = watch('city');
  const watchPostCode = watch('postCode');
  const watchCountryCode = watch('countryCode');
  const watchAddressCountryCode = watch('addressCountryCode');

  const resetAndFocus = (field: keyof EditPartnerFormFields) => {
    resetField(field, { defaultValue: '' });
    setFocus(field);
  };

  return (
    <form id={id} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing="20px">
        <FormControl isInvalid={!!errors?.name?.message} isRequired>
          <FormLabel>Partner name</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter partner name" {...register('name')} />
            {watchName && (
              <InputRightElement h="full" cursor="pointer" onClick={() => resetAndFocus('name')}>
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.companyNumber?.message} isRequired>
          <FormLabel>Company number</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter company number" {...register('companyNumber')} />
            {watchCompanyNumber && (
              <InputRightElement
                h="full"
                cursor="pointer"
                onClick={() => resetAndFocus('companyNumber')}
              >
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.companyNumber?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.countryCode?.message}>
          <FormLabel>Country</FormLabel>
          <Controller
            control={control}
            name="countryCode"
            render={({ field: { onChange, onBlur } }) => (
              <DropdownInput
                key={watchCountryCode}
                placeholder="Select a country (optional)"
                options={PARTNER_COUNTRY_OPTIONS}
                defaultValue={PARTNER_COUNTRY_OPTIONS.find((o) => o.value === watchCountryCode)}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.countryCode?.message}</FormErrorMessage>
        </FormControl>

        <Divider />
        <Text fontSize="sm" fontWeight="semibold" color="brand.darkGray">
          Address
        </Text>

        <FormControl isInvalid={!!errors?.addressLine1?.message}>
          <FormLabel>Address line 1</FormLabel>
          <InputGroup>
            <Input
              type="text"
              placeholder="Enter address line 1 (optional)"
              {...register('addressLine1')}
            />
            {watchAddressLine1 && (
              <InputRightElement
                h="full"
                cursor="pointer"
                onClick={() => resetAndFocus('addressLine1')}
              >
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.addressLine1?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.addressLine2?.message}>
          <FormLabel>Address line 2</FormLabel>
          <InputGroup>
            <Input
              type="text"
              placeholder="Enter address line 2 (optional)"
              {...register('addressLine2')}
            />
            {watchAddressLine2 && (
              <InputRightElement
                h="full"
                cursor="pointer"
                onClick={() => resetAndFocus('addressLine2')}
              >
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.addressLine2?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.addressLine3?.message}>
          <FormLabel>Address line 3</FormLabel>
          <InputGroup>
            <Input
              type="text"
              placeholder="Enter address line 3 (optional)"
              {...register('addressLine3')}
            />
            {watchAddressLine3 && (
              <InputRightElement
                h="full"
                cursor="pointer"
                onClick={() => resetAndFocus('addressLine3')}
              >
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.addressLine3?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.city?.message}>
          <FormLabel>City</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter city (optional)" {...register('city')} />
            {watchCity && (
              <InputRightElement h="full" cursor="pointer" onClick={() => resetAndFocus('city')}>
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.city?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.postCode?.message}>
          <FormLabel>Post code</FormLabel>
          <InputGroup>
            <Input type="text" placeholder="Enter post code (optional)" {...register('postCode')} />
            {watchPostCode && (
              <InputRightElement
                h="full"
                cursor="pointer"
                onClick={() => resetAndFocus('postCode')}
              >
                <CrossOutline color="brand.darkGray" boxSize="24px" />
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>{errors?.postCode?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors?.addressCountryCode?.message}>
          <FormLabel>Address country</FormLabel>
          <Controller
            control={control}
            name="addressCountryCode"
            render={({ field: { onChange, onBlur } }) => (
              <DropdownInput
                key={watchAddressCountryCode}
                placeholder="Select a country (optional)"
                options={PARTNER_COUNTRY_OPTIONS}
                defaultValue={PARTNER_COUNTRY_OPTIONS.find(
                  (o) => o.value === watchAddressCountryCode
                )}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.addressCountryCode?.message}</FormErrorMessage>
        </FormControl>

        <Stack direction="row" justify="flex-end" pt="10px">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" isDisabled={!isValid || !isDirty} isLoading={isLoading}>
            Save changes
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export default EditPartnerForm;
